<template>
  <div
    class="mobile-filter-actions"
    :class="{
      'is-sticky': isSticky,
      'reverse-layout': props.activeTab !== 'custom',
    }"
  >
    <!-- Search Bar with Cart Button -->
    <div class="search-cart-row" v-if="props.activeTab === 'custom'">
      <div class="search-bar-mobile">
        <span class="search-icon">
          <img src="/images/maker-cart/union.svg" alt="Search icon" />
        </span>
        <input
          class="search-input"
          type="text"
          :value="props.searchQuery"
          @input="handleSearchInput"
          placeholder="Search activities by grade, subject, or keyword"
        />
        <button
          v-if="props.searchQuery"
          type="button"
          class="remove-icon"
          aria-label="Clear search"
          @click="clearSearch"
        >
          <img src="/images/close.svg" alt="Clear search" />
        </button>
      </div>
    </div>

    <!-- Action Buttons Row -->
    <div class="actions-row">
      <div class="action-row-filters">
        <button
          v-if="props.activeTab === 'custom'"
          type="button"
          class="action-button filter-button"
          aria-label="Open filters"
          @click="filterClicked"
        >
          <FilterIcon />
        </button>
        <div class="sort-selector" v-if="activeTab !== 'makercrate'">
          <SingleSelect
            :options="sortOptions"
            prefix="Sort by: "
            :model-value="props.selectedSort"
            @update:model-value="$emit('update:selectedSort', $event)"
            @change="onSortSelectChange"
          />
        </div>
        <div class="locale-selector">
          <LocaleSelector
            :model-value="props.selectedLocale"
            @update:model-value="$emit('update:selectedLocale', $event)"
            :is-singapore-disabled="
              props.activeTab === 'signature' ||
              props.activeTab === 'makercrate'
            "
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, computed } from "vue";
import SingleSelect from "@/components/common/SingleSelect.vue";
import LocaleSelector from "@/components/common/LocaleSelector.vue";
import { FilterIcon } from "@/components/icons";

const props = defineProps({
  searchQuery: {
    type: String,
    default: "",
  },
  selectedSort: {
    type: String,
    default: "top-activities",
  },
  selectedLocale: {
    type: String,
    default: "US",
  },
  isSticky: {
    type: Boolean,
    default: false,
  },
  activeTab: {
    type: String,
    default: "custom",
  },
});

const emit = defineEmits([
  "update:searchQuery",
  "update:selectedSort",
  "update:selectedLocale",
  "search",
  "clear-search",
  "filter-clicked",
  "sort-changed",
  "locale-changed",
]);

const sortOptions = computed(() => {
  const options = [
    { value: "newest", text: "<b><strong>Newest</strong></b>" },
    { value: "oldest", text: "<b><strong>Oldest</strong></b>" },
    { value: "a-z", text: "<b><strong>A-Z</strong></b>" },
    { value: "z-a", text: "<b><strong>Z-A</strong></b>" },
  ];

  if (props.activeTab === "custom") {
    return [
      { value: "top-activities", text: "<b><strong>Popular</strong></b>" },
      ...options,
    ];
  }

  return options;
});

const handleSearchInput = (event) => {
  emit("update:searchQuery", event.target.value);
  emit("search", event.target.value);
};

const clearSearch = () => {
  emit("update:searchQuery", "");
  emit("clear-search");
};

const filterClicked = () => {
  emit("filter-clicked");
};

const onSortSelectChange = (payload) => {
  if (!payload || !payload.value) return;
  emit("update:selectedSort", payload.value);
  emit("sort-changed", payload.value);
};
</script>

<style scoped>
.mobile-filter-actions {
  background: white;
  padding: 8px 16px;
  transition: all 0.3s ease;
  position: relative;
  z-index: 10;
}

.mobile-filter-actions.reverse-layout {
  margin-top: 8px;
  display: flex;
  flex-direction: row-reverse;
}

.mobile-filter-actions.is-sticky {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.search-cart-row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}

.search-bar-mobile {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #6c737f;
  border-radius: 8px;
  height: 56px;
  overflow: hidden;
}

.search-icon {
  position: absolute;
  left: 16px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-icon img {
  width: 24px;
  height: 24px;
}

.search-input {
  flex: 1;
  padding: 4px 16px 4px 48px;
  border: none;
  outline: none;
  background: transparent;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 500;
  color: #374151;
  height: 100%;
}

.search-input::placeholder {
  color: #98a2b3;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 500;
}

.remove-icon {
  position: absolute;
  right: 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  z-index: 2;
}

.remove-icon img {
  width: 16px;
  height: 16px;
}

.actions-row {
  width: 100%;
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
}

.action-row-filters {
  display: flex;
  gap: 8px;
  align-items: left;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  border: 1px solid var(--primary-blue);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "League Spartan", sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: var(--primary-blue);
  height: 32px;
  white-space: nowrap;
}

.action-button:hover {
  background: #f2f3fb;
}

.action-button img {
  width: 10px;
  height: 10px;
}

.filter-button {
  flex-shrink: 0;
  width: 32px;
  padding: 10px;
}

/* Override SingleSelect styles for mobile */
.sort-selector :deep(.single-select) {
  width: 100%;
}

.sort-selector :deep(.select-trigger) {
  height: 32px;
  padding: 6px 12px;
  font-size: var(--fs-medium);
  border-color: var(--primary-blue);
}

.sort-selector :deep(.select-text) {
  font-size: var(--fs-medium);
  font-weight: 700;
  color: var(--primary-blue);
}

.sort-selector :deep(.prefix-text) {
  font-size: var(--fs-medium);
  font-weight: 400;
  color: var(--primary-blue);
}

.sort-selector :deep(.prefix-text_value) {
  font-size: var(--fs-medium);
  font-weight: 700;
  color: var(--primary-blue);
}

/* Override LocaleSelector styles for mobile */
.locale-selector :deep(.locale-selector) {
  width: 100%;
}

.locale-selector :deep(.select-trigger) {
  height: 32px;
  min-height: 32px;
  padding: 6px 12px;
  font-size: var(--fs-medium);
  border-color: var(--primary-blue);
  min-width: auto;
  box-sizing: border-box;
}

.locale-selector :deep(.select-text) {
  font-size: var(--fs-medium);
  font-weight: 700;
  color: var(--primary-blue);
}

.locale-selector :deep(.option-text) {
  font-size: var(--fs-small);
}

.locale-selector :deep(.flag-icon) {
  width: 16px;
  height: 16px;
  border-radius: 50%;
}

.sort-selector :deep(.select-dropdown .option-text) {
  font-size: var(--fs-small);
}

/* Keep sort menu on-screen on mobile (avoid shared SingleSelect right-align clip) */
.sort-selector :deep(.select-dropdown),
.sort-selector :deep(.select-dropdown.right-aligned) {
  left: 0 !important;
  right: auto !important;
  min-width: 160px;
  max-width: calc(100vw - 24px);
}

/* Mobile specific adjustments */
@media (max-width: 480px) {
  .mobile-filter-actions {
    padding: 8px 12px;
  }

  .action-button {
    padding: 8px;
    font-size: var(--fs-small);
  }

  .sort-selector :deep(.select-trigger) {
    padding: 4px 8px;
    font-size: var(--fs-small);
  }

  .sort-selector :deep(.select-text),
  .sort-selector :deep(.prefix-text),
  .sort-selector :deep(.prefix-text_value),
  .sort-selector :deep(.option-text) {
    font-size: var(--fs-small);
  }

  .locale-selector :deep(.select-trigger) {
    padding: 4px 8px;
    font-size: var(--fs-small);
    height: 32px;
    min-height: 32px;
  }

  .locale-selector :deep(.select-text),
  .locale-selector :deep(.option-text) {
    font-size: var(--fs-small);
  }
}

/* Ensure proper z-index for sticky behavior */
.mobile-filter-actions.is-sticky {
  z-index: 1000;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>
